import * as Sentry from "@sentry/browser";
import { Button, McpIcon, XIcon } from "@stacklet/ui";
import { graphql, useFragment, useMutation } from "react-relay";

import { Link } from "app/components/links";
import { paths } from "app/constants/paths";
import { useMCPEnabled } from "app/hooks/feature-flags/useMCPEnabled/useMCPEnabled";
import { useHasScopePermission } from "app/hooks/rbac/useHasScopePermission";
import { useStickyState } from "app/hooks/ui/useStickyState";
import { UpsertUserDataMutation } from "app/mutations";

import type { McpInstallCallout_query$key } from "./__generated__/McpInstallCallout_query.graphql";
import type { UpsertUserDataMutation as UpsertUserDataMutationType } from "app/mutations/__generated__/UpsertUserDataMutation.graphql";

/** The `userData` name recording that the callout is spent. */
export const MCP_CALLOUT_USER_DATA_NAME = "mcp_install_callout_dismissed";

/**
 * Its type. `userData` names are unique only within a type, so the type is
 * half of the key and both halves have to match on the way in and out.
 */
export const MCP_CALLOUT_USER_DATA_TYPE = "config";

/**
 * Where this device remembers the answer, per user.
 *
 * The write does not reach the cached read: `upsertUserData` returns a bare
 * node, and nothing appends it to the `userData` connection the callout reads,
 * so a remount would otherwise find the store exactly as empty as before and
 * offer the callout again. The server stays the cross-device truth; this is
 * only what this browser already knows. It is keyed by the user's uuid
 * because one browser serves several accounts, and one user's answer is not
 * another's — the uuid is the only identifier of theirs the API promises to
 * be both stable and present.
 */
export const MCP_CALLOUT_STORAGE_KEY = "mcp-install-callout-dismissed";

interface Props {
  /** Bootstrap query ref carrying the callout's dismissal `userData`. */
  queryRef: McpInstallCallout_query$key;
}

/**
 * A one-time nudge towards the Stacklet MCP install instructions, shown in the
 * sidebar's bottom area.
 *
 * It is spent on first use: following it or dismissing it both record that in
 * `userData`, so it never returns on any of the user's devices. Three things
 * have to hold for it to appear at all — the deployment serves an MCP
 * endpoint, the user can reach the settings page it points at, and they have
 * not already answered it.
 */
export function McpInstallCallout({ queryRef }: Props) {
  const hasPermission = useHasScopePermission();

  const data = useFragment(
    graphql`
      fragment McpInstallCallout_query on Query {
        mcpCallout: userData(
          filterElement: {
            single: {
              name: "name"
              operator: "equals"
              value: "mcp_install_callout_dismissed"
            }
          }
        ) {
          edges {
            node {
              type
              value
            }
          }
        }
        whoami {
          user {
            uuid
          }
        }
        ...useMCPEnabled_query
      }
    `,
    queryRef,
  );

  const [isDismissedHere, setIsDismissedHere] = useStickyState(
    false,
    `${MCP_CALLOUT_STORAGE_KEY}:${data.whoami.user.uuid}`,
  );

  const { isEnabled } = useMCPEnabled(data);
  // The filter narrows by name only, which another type could also answer to,
  // so the type is checked here rather than trusting the first edge.
  const isDismissedOnServer = data.mcpCallout.edges.some(
    ({ node }) =>
      node.type === MCP_CALLOUT_USER_DATA_TYPE && node.value === "true",
  );
  const isDismissed = isDismissedOnServer || isDismissedHere;
  const [commitDismissal] = useMutation<UpsertUserDataMutationType>(
    UpsertUserDataMutation,
  );

  /**
   * Hides the callout and records it as answered, here and on the server.
   *
   * A failed write is reported but not rolled back: putting the callout back
   * under a user who just closed it is worse than what an unrecorded
   * dismissal actually costs, which is the callout returning on their other
   * devices. This one has already remembered.
   */
  const dismiss = () => {
    setIsDismissedHere(true);
    commitDismissal({
      variables: {
        input: {
          name: MCP_CALLOUT_USER_DATA_NAME,
          type: MCP_CALLOUT_USER_DATA_TYPE,
          value: "true",
        },
      },
      onCompleted(_, errors) {
        if (errors) {
          Sentry.captureException(
            new Error("Failed to record the MCP install callout dismissal", {
              cause: errors,
            }),
          );
        }
      },
      onError(error) {
        Sentry.captureException(error);
      },
    });
  };

  if (!isEnabled || isDismissed || !hasPermission("system", "EDIT")) {
    return null;
  }

  return (
    <div className="flex h-7 w-full items-center gap-xs rounded-1 border border-outline-informative bg-background-informative px-md py-xxs">
      {/*
        The label takes the row's width so most of the pill navigates, leaving
        the close control its own hit area rather than nesting one button
        inside another.
      */}
      <Link
        className="flex flex-1 items-center gap-xs text-label-small-strong capitalize text-text-informative"
        onPress={dismiss}
        textDecoration="none"
        to={paths.settingsMcp}
      >
        <McpIcon size={14} />
        Install MCP
      </Link>

      <Button
        aria-label="Dismiss the Install MCP prompt"
        className="p-0 text-text-informative hover:bg-transparent"
        onPress={dismiss}
        variant="plain"
      >
        <XIcon size={14} />
      </Button>
    </div>
  );
}
